import React, { useState, useEffect } from 'react';
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import {Modal, IconButton} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import InfoIcon from '@mui/icons-material/Info';
import {SupportTicket, SupportTicketStatusList} from "../../../models/support";
import {fetchSupportTicketsPaginated, deleteSupportTicket} from "../../../components/business_logic/support_ticket_requests";
import ViewSupportTicket from "./preview_support_ticket";
import NewSupportTicket from "./new_support_ticket";
import DeleteItemDialog from "../templates/delete_item_dialog";
import CircularProgress from "@mui/material/CircularProgress";

export default function SupportTicketsCatalog() {
    const [supportTickets, setSupportTickets] = useState<SupportTicket[]>([]);
    const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
    const [ticketToDelete, setTicketToDelete] = useState<SupportTicket | null>(null);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [newTicketOpen, setNewTicketOpen] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadTickets().then();
    }, []);

    const loadTickets = async () => {
        setLoading(true);
        try {
            const tickets = await fetchSupportTicketsPaginated(1);
            // Sort by updated date (newest first)
            const sorted = tickets.sort((a, b) => {
                const dateA = a.updated_at ? new Date(a.updated_at).getTime() : 0;
                const dateB = b.updated_at ? new Date(b.updated_at).getTime() : 0;
                return dateB - dateA; // Descending order (newest first)
            });
            setSupportTickets(sorted);
        } catch (error) {
            console.error('Failed to load support tickets:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleTicketClick = (ticket: SupportTicket) => {
        setSelectedTicket(ticket);
        setPreviewOpen(true);
    };

    const handleClosePreview = () => {
        setPreviewOpen(false);
        setSelectedTicket(null);
    };

    const handleOpenNewTicket = () => {
        setNewTicketOpen(true);
    };

    const handleCloseNewTicket = () => {
        setNewTicketOpen(false);
    };

    const handleTicketCreated = () => {
        loadTickets().then();
    };

    const handleDeleteClick = (e: React.MouseEvent, ticket: SupportTicket) => {
        e.stopPropagation(); // Prevent card click
        setTicketToDelete(ticket);
    };

    const formatDate = (dateString?: string) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch {
            return dateString;
        }
    };

    const formatStatus = (status?: string) => {
        if (!status) return 'N/A';
        const statusOption = SupportTicketStatusList.find(s => s.value === status);
        return statusOption ? statusOption.label : status.split('_').map(word => 
            word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');
    };

    const getStatusColor = (status?: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
        switch (status) {
            case 'open':
                return 'info';
            case 'in_progress':
                return 'warning';
            case 'resolved':
                return 'success';
            case 'rejected':
                return 'error';
            default:
                return 'default';
        }
    };


    if (loading) {
        return (
            <Container sx={{ py: 4, textAlign: 'center' }}>
                <CircularProgress />
                <Typography sx={{ mt: 2 }}>Loading support tickets...</Typography>
            </Container>
        );
    }

    return (
        <Container sx={{ py: 4 }}>
            {/* Header Section */}
            <Box sx={{ mb: 4 }}>
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    mb: 3,
                    flexWrap: 'wrap',
                    gap: 2
                }}>
                    <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
                        Support Center
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleOpenNewTicket}
                        size="large"
                    >
                        New Support Ticket
                    </Button>
                </Box>

                {/* Information Section */}
                <Card variant="outlined" sx={{ mb: 4, backgroundColor: 'action.hover' }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            <InfoIcon sx={{ color: 'primary.main', mt: 0.5 }} />
                            <Box>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                                    Need Help?
                                </Typography>
                                <Typography variant="body2" color="text.secondary" paragraph>
                                    Our support team is here to help you with any questions or issues you may have. 
                                    Create a support ticket to get assistance, or view your existing tickets below.
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    <strong>Response Time:</strong> We typically respond within 24-48 hours during business days.
                                </Typography>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            </Box>

            {/* Tickets Section */}
            {supportTickets.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                    <SupportAgentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        No Support Tickets Yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        You haven't created any support tickets. Click the button above to create your first ticket.
                    </Typography>
                    <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={handleOpenNewTicket}
                    >
                        Create Support Ticket
                    </Button>
                </Box>
            ) : (
                <>
                    <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                        Your Support Tickets ({supportTickets.length})
                    </Typography>
                    <Grid container spacing={3}>
                        {supportTickets.map((ticket) => (
                            <Grid 
                                key={ticket.id} 
                                size={{ xs: 12, sm: 6, md: 4 }}
                            >
                                <Card
                                    variant="outlined"
                                    sx={{
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease-in-out',
                                        '&:hover': {
                                            boxShadow: 4,
                                            transform: 'translateY(-2px)',
                                        },
                                        borderColor: 'divider',
                                    }}
                                    onClick={() => handleTicketClick(ticket)}
                                >
                                    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                            <Typography 
                                                variant="h6" 
                                                component="h2" 
                                                sx={{ 
                                                    fontWeight: 600,
                                                    flex: 1,
                                                    mr: 1
                                                }}
                                            >
                                                {ticket.title || 'Untitled Ticket'}
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                <Chip 
                                                    label={formatStatus(ticket.ticket_status)}
                                                    size="small" 
                                                    color={getStatusColor(ticket.ticket_status)}
                                                    sx={{ flexShrink: 0 }}
                                                />
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => handleDeleteClick(e, ticket)}
                                                    sx={{
                                                        color: 'error.main',
                                                        '&:hover': {
                                                            backgroundColor: 'error.light',
                                                            color: 'error.dark',
                                                        },
                                                    }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        </Box>

                                        <Box sx={{ 
                                            display: 'flex', 
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            mt: 'auto',
                                            pt: 2,
                                            borderTop: '1px solid',
                                            borderColor: 'divider'
                                        }}>
                                            <Typography variant="caption" color="text.secondary">
                                                {formatDate(ticket.updated_at || ticket.created_at)}
                                            </Typography>
                                            <Typography variant="caption" color="primary">
                                                View details →
                                            </Typography>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </>
            )}

            {/* Preview Modal */}
            <Modal 
                open={previewOpen && !!selectedTicket}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleClosePreview();
                    }
                }}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2,
                }}
            >
                <Box 
                    sx={{
                        position: 'relative',
                        width: "90%",
                        maxWidth: "900px",
                        maxHeight: "90vh",
                        backgroundColor: 'background.paper',
                        borderRadius: 2,
                        boxShadow: 24,
                        outline: 'none',
                        overflow: 'auto',
                        border: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    <IconButton
                        onClick={handleClosePreview}
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            color: 'white',
                            zIndex: 1,
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                            },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    {selectedTicket && <ViewSupportTicket ticket={selectedTicket} isAdmin={false} />}
                </Box>
            </Modal>

            {/* New Ticket Modal */}
            <NewSupportTicket 
                open={newTicketOpen}
                onClose={handleCloseNewTicket}
                onSuccess={handleTicketCreated}
            />

            {/* Delete Confirmation Dialog */}
            <DeleteItemDialog
                itemToDelete={ticketToDelete}
                setItemToDelete={setTicketToDelete}
                onDelete={() => deleteSupportTicket(ticketToDelete!)}
                onSuccess={() => {
                    loadTickets().then();
                }}
                itemName={(ticket) => ticket.title || 'Untitled Ticket'}
                itemDetails={(ticket) => (
                    <>
                        <Typography variant="body2" color="text.secondary">
                            ID: {ticket.id}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Status: {formatStatus(ticket.ticket_status)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Created: {formatDate(ticket.created_at)}
                        </Typography>
                    </>
                )}
                title="Delete Support Ticket"
                description="Are you sure you want to delete this support ticket?"
                warningMessage="⚠️ This action cannot be undone and will permanently delete this support ticket."
                requireNameVerification={false}
            />
        </Container>
    );
}
